import * as React from 'react';
import { ProductImages } from 'elc-images';
import { observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import { ContentSpacing, Breakpoints } from 'elc-base-theme';
import { root } from 'elc-universal-utils';
import { serviceNames, diContainer } from '../../../service-setup/diContainer';
import { ProductBriefViewController } from '../../controllers/ProductBriefViewController';
import { ProductItemWrapper } from '../common/ProductItemWrapper';
import { Desktop, Mobile } from 'elc-layouts';
import { classes } from '../../utils/Classes';
import { ADD_TO_BAG } from '../../constants/CTATypes';
import { ProductBadge } from '../product-badge/ProductBadge';
import { ProductBriefDetail } from './ProductBriefDetail';
import { MPP_ADD_TO_BAG } from '../../constants/DataTestIdAttributes';
import { TOGGLE_TYPE } from '../../constants/AutoReplenish';
import { BackgroundWhiteTheme, ProductBriefShadowTheme } from '../../../theme/default-theme';
import {
    IProductImages,
    ITranslationsOverwrites
} from '../../../exported/product-grid-wrapper/ProductGridWrapper';
import { IFeatures } from '../../constants/Features';
import { config, translate } from 'elc-service';
import { FavoritesIcon } from '../product-favorites/FavoritesIcon';
import { ComponentTypes } from '../../constants/ComponentTypes';
import { action, observable } from 'mobx';
import { CheckVisibility } from 'elc-check-visibility';
import { IProductInstallmentsConfig } from '../../../internal/views/product-installments-price/ProductInstallmentsPrice';
import { DEFAULT_IMAGE_CAROUSEL_SETTINGS } from '../../constants/ImageThumbnailCarouselSettings';
import { SliderView } from 'elc-slider';
import { IAsset } from '../../domain/entities/Asset';
import { IShadeSwatches } from '../../interfaces/IShadePicker';
import {
    ISizePickerImages,
    IShortDescriptionConfig,
    IImageCarouselSettings,
    IProductBenefitTypes
} from '../../interfaces/IProdcat';
import { Product } from '../../domain/entities/Product';
import { ProductBenefitType } from '../product-benefit-type/ProductBenefitType';
import { IProductImageBadge } from '../product-image-badge/ProductImageBadge';

export interface IProductBriefDefaulProps {
    productId: string;
    hideProductPrice?: boolean;
    hideProductPriceRange?: boolean;
    showSubDisplayName?: boolean;
    hideProductRating?: boolean;
    ctaType?: string;
    enableNotifyMe?: boolean;
    enableQuickView?: boolean;
    enableMobileQuickView?: boolean;
    enableShadePicker?: boolean;
    enableNumberShades?: boolean;
    enableSizePicker?: boolean;
    shortDescriptionLocation?: IShortDescriptionConfig;
    enableProductDetailLink?: boolean;
    useInitialRepositoryInventory?: boolean;
    autoReplenishOptions: number[];
}

export interface IProductBriefConfig {
    features?: IFeatures;
    installments?: IProductInstallmentsConfig;
    imageCarouselSettings?: IImageCarouselSettings;
    shadeSwatches?: IShadeSwatches;
}

export interface IProductBrief extends IProductImageBadge {
    className?: string;
    hideProductPrice?: boolean;
    hideProductPriceRange?: boolean;
    showSubDisplayName?: boolean;
    hideProductRating?: boolean;
    ctaType?: string;
    enableNotifyMe?: boolean;
    enableQuickView?: boolean;
    enableMobileQuickView?: boolean;
    enableHoverInteraction?: boolean;
    hideReviewsNumber?: boolean;
    productGridHideReadReviewsLink?: boolean;
    enableShadePicker?: boolean;
    enableNumberShades?: boolean;
    enableSizePicker?: boolean;
    shortDescriptionLocation?: IShortDescriptionConfig;
    enableProductDetailLink?: boolean;
    onImageLoaded?(): void;
    useInitialRepositoryInventory?: boolean;
    enableQuickViewShortcut?: boolean;
    imageOverride?: string;
    isFeatured?: boolean;
    enableLearnMoreLink?: boolean;
    autoReplenishOptions: number[];
    autoReplenishToggleType?: TOGGLE_TYPE;
    viewableInventoryStatuses?: string[];
    sizePickerImages?: ISizePickerImages;
    hideProductPricePerUnit?: boolean;
    showProductQuantitySelector?: boolean;
    hideEmptyStars?: boolean;
    enableAutoReplenishment?: boolean;
    translationsOverwrites?: ITranslationsOverwrites;
    isLoaded?: boolean;
    maxHeight?: number;
    lazyLoadGridItem?: boolean;
    config?: IProductBriefConfig;
    productImages?: IProductImages;
    positionIndex?: number;
    translations?: IProductBriefTranslations;
    product: Product;
    productBenefitTypes?: IProductBenefitTypes;
}

interface IProductBriefTranslations {
    quickViewButtonLabel: string;
}

interface IWrapperProps {
    maxHeight: number;
}

interface IImgCommonProps {
    onClick(): Promise<void>;
    onImageLoaded?(): void;
    lazyLoad?: boolean;
}

const hiddenComponentsStyle = css`
    .elc-product-quick-view-button,
    .elc-product-description-wrapper,
    .elc-product-shades-number-wrapper,
    .elc-product-quick-view-shortcut {
        display: none;
    }
`;

const hoverComponentsStyle = css`
    .elc-product-product-name-wrapper,
    .elc-product-price-wrapper {
        display: none;
    }
    .elc-rating,
    .elc-product-quick-view-button,
    .elc-product-description-wrapper,
    .elc-product-shades-number-wrapper,
    .elc-product-quick-view-shortcut {
        display: initial;
    }
`;

const StyledProductItemWrapper = styled(ProductItemWrapper)`
    position: 1;
    flex: 1;
`;

const Wrapper = styled.div`
    max-width: 770px;
    height: 100%;
    text-align: center;
    transition: 0.1s;
    padding: ${ContentSpacing.space8};
    margin: 0 auto;
    position: relative;
    border-radius: 4px;
    box-sizing: border-box;
    ${ProductBriefShadowTheme};
    ${BackgroundWhiteTheme};
    display: flex;
    flex-direction: column;

    .elc-product-images-wrapper {
        height: auto;
    }

    .elc-product-detail-wrapper {
        flex: 1;
        height: auto;
    }

    &.elc-hover-interaction {
        /* smartphones, touchscreens */
        @media (hover: none) and (pointer: coarse) {
            ${hiddenComponentsStyle}
            .elc-rating {
                display: block;
            }
        }

        /* mouse, touch pad */
        @media (hover: hover) and (pointer: fine) {
            ${hiddenComponentsStyle}
            .elc-rating {
                display: none;
            }

            &:hover,
            &:focus,
            &:focus-within {
                ${hoverComponentsStyle}
            }
        }
    }

    &.elc-product-brief-wrapper:hover .elc-product-brief-favorites-icon-wrapper {
        display: block;
    }
`;

const InitialWrapper = styled(Wrapper)<IWrapperProps>`
    min-height: 500px;
    height: ${props => (props.maxHeight ? `${props.maxHeight}px` : '100%')};
`;

const FavoritesIconWrapper = styled.div`
    /* smartphones, touchscreens */
    @media (hover: none) and (pointer: coarse) {
        display: block;
    }

    display: none;
    position: absolute;
    right: 0;

    [dir='rtl'] & {
        left: 0;
    }
`;

@config(['features', 'imageCarouselSettings', 'installments'])
@translate(['quickViewButtonLabel'])
@observer
export class ProductBrief extends React.Component<IProductBrief> {
    @observable private isItemInView: boolean = false;
    public static defaultProps: IProductBriefDefaulProps;
    private productBriefViewController: ProductBriefViewController;
    private styledProductImagesContainer: string = classes('styled-product-image-container');
    private productBriefImageSliderImagesWrapper = classes(
        'product-brief-image-slider-images-wrapper'
    );
    private productImageBadgeWrapper = classes('product-image-badge-wrapper');

    public constructor(props: IProductBrief) {
        super(props);
        this.productBriefViewController = diContainer.get(serviceNames.productBriefViewController);
        this.productBriefViewController.setProduct(this.props.product);
    }

    public componentDidUpdate(prevProps: IProductBrief) {
        if (this.props.product.productId !== prevProps.product.productId) {
            this.productBriefViewController.setProduct(this.props.product);
        }
    }

    private navToProd = async () => {
        const { positionIndex } = this.props;
        await this.productBriefViewController.navigateToProduct(positionIndex as number);
    };

    @action
    private onVisible = () => {
        this.isItemInView = true;
    };

    private getSliderImages = (images: IAsset[], imgCommonProps: IImgCommonProps) => {
        const { product } = this.props;
        const { selectedSku } = this.productBriefViewController.data;

        const gridImageCarouselSettings =
            this.props.config && this.props.config.imageCarouselSettings;

        const hideImageBadge =
            gridImageCarouselSettings &&
            gridImageCarouselSettings[ComponentTypes.PRODUCT_GRID] &&
            gridImageCarouselSettings[ComponentTypes.PRODUCT_GRID].hideImageBadge;

        const productBadgeComponent = !hideImageBadge && (
            <ProductBadge product={product} selectedSku={selectedSku} />
        );

        return images.map((image: IAsset, index: number) => {
            const isFirstImage = index === 0;

            return (
                <div
                    key={`${image.src}-${index}`}
                    className={this.productBriefImageSliderImagesWrapper}
                >
                    {isFirstImage && productBadgeComponent}
                    <ProductImages {...imgCommonProps} imgPath={image.src} altText={image.alt} />
                </div>
            );
        });
    };

    private renderImage = () => {
        const {
            alternateImage,
            largeImages,
            productBriefImageCarouselSettings,
            isMobileImageCarouselEnabled,
            isDesktopImageCarouselEnabled
        } = this.productBriefViewController.data;
        const { onImageLoaded, imageOverride, isFeatured, productImages } = this.props;
        const enableLazyLoadGridImages =
            productImages && productImages.product_grid && productImages.product_grid.lazyload;
        const customSizeImages = this.productBriefViewController.getCustomSizeImages(
            ComponentTypes.PRODUCT_GRID
        );
        const productImageDesktop = customSizeImages.desktop.defaultImage;
        const productImageMobile = customSizeImages.mobile.defaultImage;

        const productImgCommonProps: IImgCommonProps = {
            onClick: this.navToProd,
            onImageLoaded: onImageLoaded,
            lazyLoad: enableLazyLoadGridImages
        };

        const sliderImages = this.getSliderImages(largeImages, productImgCommonProps);

        const imageCarouselSettings = {
            ...DEFAULT_IMAGE_CAROUSEL_SETTINGS,
            ...productBriefImageCarouselSettings
        };

        const { product } = this.props;
        const { selectedSku } = this.productBriefViewController.data;

        const gridImageCarouselSettings =
            this.props.config && this.props.config.imageCarouselSettings;
        const hideImageBadge =
            gridImageCarouselSettings &&
            gridImageCarouselSettings.productGrid &&
            gridImageCarouselSettings.productGrid.hideImageBadge;

        const productBadgeComponent = !hideImageBadge && (
            <ProductBadge product={product} selectedSku={selectedSku} />
        );

        return (
            <>
                <Mobile maxWidth={Breakpoints.desktop - 1}>
                    {() =>
                        isMobileImageCarouselEnabled ? (
                            <SliderView
                                sliderSettings={imageCarouselSettings}
                                items={sliderImages}
                            />
                        ) : (
                            <div className={this.productImageBadgeWrapper}>
                                <ProductImages
                                    {...productImgCommonProps}
                                    imgPath={imageOverride || productImageMobile.src}
                                    altText={productImageMobile.alt}
                                />
                                {productBadgeComponent}
                            </div>
                        )
                    }
                </Mobile>
                <Desktop minWidth={Breakpoints.desktop}>
                    {() =>
                        isDesktopImageCarouselEnabled ? (
                            <SliderView
                                sliderSettings={imageCarouselSettings}
                                items={sliderImages}
                            />
                        ) : (
                            <div className={this.productImageBadgeWrapper}>
                                <ProductImages
                                    {...productImgCommonProps}
                                    imgPath={imageOverride || productImageDesktop.src}
                                    altImgPath={!isFeatured ? alternateImage : ''}
                                    altText={productImageDesktop.alt}
                                />
                                {productBadgeComponent}
                            </div>
                        )
                    }
                </Desktop>
            </>
        );
    };

    private renderBriefWrapper() {
        const { maxHeight = 0 } = this.props;
        const briefWrapperClassNames = classes('product-brief-wrapper');

        return (
            <CheckVisibility onVisible={this.onVisible}>
                <InitialWrapper className={briefWrapperClassNames} maxHeight={maxHeight} />
            </CheckVisibility>
        );
    }

    private renderBrief() {
        const {
            data,
            selectShade,
            selectSize,
            handleFavoriteClick
        } = this.productBriefViewController;

        const {
            productUrl,
            isUpdatedInventorySource,
            largeImages,
            selectedSku,
            isProductQuickViewOpen
        } = data;

        const {
            hideProductPrice,
            hideProductPriceRange,
            hideProductRating,
            ctaType,
            enableNotifyMe,
            enableQuickView,
            enableMobileQuickView,
            enableHoverInteraction,
            hideReviewsNumber,
            productGridHideReadReviewsLink,
            enableShadePicker,
            enableNumberShades,
            enableSizePicker,
            shortDescriptionLocation,
            enableProductDetailLink,
            useInitialRepositoryInventory,
            enableQuickViewShortcut,
            enableLearnMoreLink,
            autoReplenishOptions,
            autoReplenishToggleType,
            className,
            sizePickerImages,
            hideProductPricePerUnit,
            showProductQuantitySelector,
            hideEmptyStars,
            enableAutoReplenishment,
            translationsOverwrites,
            product,
            isLoaded,
            positionIndex,
            productBenefitTypes
        } = this.props;
        const briefConfig = this.props.config as IProductBriefConfig;

        const { productId, displayName } = product;
        const productBriefClassNames = classes(
            'product-brief-wrapper',
            className,
            enableHoverInteraction ? 'elc-hover-interaction' : '',
            isLoaded ? 'product-brief-data-loaded' : ''
        );

        const briefFavoritesIconWrapperClass = classes('product-brief-favorites-icon-wrapper');

        const featuresConfig = briefConfig && briefConfig.features;
        const enableFavorites =
            featuresConfig && featuresConfig.favorites && featuresConfig.favorites.enabled;

        const skuBaseId = selectedSku.perlgem.SKU_BASE_ID || selectedSku.skuId;

        return (
            <Wrapper
                className={productBriefClassNames}
                data-sku-base-id={skuBaseId}
                data-product-id={productId}
            >
                {!!productBenefitTypes && (
                    <ProductBenefitType
                        benefitTypes={productBenefitTypes}
                        skuId={selectedSku.skuId}
                        productId={productId}
                    />
                )}
                {enableFavorites && (
                    <FavoritesIconWrapper
                        className={briefFavoritesIconWrapperClass}
                        onClick={handleFavoriteClick}
                    >
                        <FavoritesIcon isFavorite={selectedSku.isFavorite} />
                    </FavoritesIconWrapper>
                )}
                <a
                    className={this.styledProductImagesContainer}
                    href={productUrl}
                    title={displayName}
                >
                    {this.renderImage()}
                </a>
                <StyledProductItemWrapper component="detail">
                    <ProductBriefDetail
                        selectedSku={selectedSku}
                        hideProductPrice={hideProductPrice}
                        hideProductPriceRange={hideProductPriceRange}
                        onProductDescriptionClick={this.navToProd}
                        onShopNowClick={this.navToProd}
                        hideProductRating={hideProductRating}
                        ctaType={ctaType}
                        enableNotifyMe={enableNotifyMe}
                        enableQuickView={enableQuickView}
                        enableMobileQuickView={enableMobileQuickView}
                        handleShadeSelect={selectShade}
                        handleSizeSelect={selectSize}
                        hideReviewsNumber={hideReviewsNumber}
                        productGridHideReadReviewsLink={productGridHideReadReviewsLink}
                        enableShadePicker={enableShadePicker}
                        enableNumberShades={enableNumberShades}
                        enableSizePicker={enableSizePicker}
                        shortDescriptionLocation={shortDescriptionLocation}
                        enableProductDetailLink={enableProductDetailLink}
                        useInitialRepositoryInventory={useInitialRepositoryInventory}
                        isUpdatedInventorySource={isUpdatedInventorySource}
                        enableQuickViewShortcut={enableQuickViewShortcut}
                        largeImages={largeImages}
                        enableLearnMoreLink={enableLearnMoreLink}
                        addToBagDataTestId={MPP_ADD_TO_BAG}
                        autoReplenishToggleType={autoReplenishToggleType}
                        autoReplenishOptions={autoReplenishOptions}
                        sizePickerImages={sizePickerImages}
                        hideProductPricePerUnit={hideProductPricePerUnit}
                        showProductQuantitySelector={showProductQuantitySelector}
                        hideEmptyStars={hideEmptyStars}
                        enableAutoReplenishment={enableAutoReplenishment}
                        translationsOverwrites={translationsOverwrites}
                        productUrl={productUrl}
                        positionIndex={positionIndex}
                        onQuickViewButtonClick={
                            this.productBriefViewController.onQuickViewButtonClick
                        }
                        onProductQuickViewClose={
                            this.productBriefViewController.closeProductQuickView
                        }
                        product={product}
                        isProductQuickViewOpen={isProductQuickViewOpen}
                    />
                </StyledProductItemWrapper>
            </Wrapper>
        );
    }

    public render() {
        return this.props.lazyLoadGridItem && !this.isItemInView && !root.__ssr
            ? this.renderBriefWrapper()
            : this.renderBrief();
    }
}

ProductBrief.defaultProps = {
    productId: '39778',
    hideProductPrice: false,
    hideProductPriceRange: false,
    showSubDisplayName: false,
    hideProductRating: false,
    ctaType: ADD_TO_BAG,
    enableNotifyMe: false,
    enableQuickView: false,
    enableMobileQuickView: false,
    enableShadePicker: true,
    enableSizePicker: true,
    shortDescriptionLocation: {
        product_brief: true
    },
    enableProductDetailLink: true,
    useInitialRepositoryInventory: true,
    autoReplenishOptions: []
};
